import React, { useState } from "react";
import WebsiteClientFeedbackCart from "./WebsiteClientFeedbackCart";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Mousewheel, Keyboard } from "swiper";
import {
  FiCheck,
  FiCheckCircle,
  FiEye,
  FiPenTool,
  FiSearch,
  FiSend,
} from "react-icons/fi";
import "swiper/css";
import "swiper/css/navigation";

const processSteps = [
  {
    name: "Discovery",
    label: "Align on the opportunity",
    icon: FiSearch,
    description:
      "We start by understanding your audience, goals, offer, and technical needs. Together, we define what success should look like before design or development begins.",
    outcomes: ["Goals and priorities", "Audience insights", "A focused project roadmap"],
  },
  {
    name: "Design",
    label: "Shape the experience",
    icon: FiPenTool,
    description:
      "We turn the strategy into a clear visual direction and intuitive page experience, balancing your brand personality with what users need to act confidently.",
    outcomes: ["Page structure", "Visual direction", "Responsive interface design"],
  },
  {
    name: "Review",
    label: "Refine it together",
    icon: FiEye,
    description:
      "You stay closely involved as the experience takes shape. We gather your feedback, explain key decisions, and refine the work before moving into final production.",
    outcomes: ["Collaborative feedback", "Clear design revisions", "Approval before development"],
  },
  {
    name: "Testing",
    label: "Make every detail dependable",
    icon: FiCheckCircle,
    description:
      "We test functionality, responsiveness, compatibility, and performance across modern browsers and devices so your website is ready for real users.",
    outcomes: ["Cross-device checks", "Functional quality assurance", "Performance review"],
  },
  {
    name: "Launch",
    label: "Go live with confidence",
    icon: FiSend,
    description:
      "Once everything is approved, we prepare the production environment, launch your website carefully, and verify that the live experience works as intended.",
    outcomes: ["Production deployment", "Post-launch verification", "A smooth final handover"],
  },
];

const WebsiteClientFeedback = () => {
  const [selectedImage, setSelectedImage] = useState(0);
  const selectedStep = processSteps[selectedImage];
  const SelectedStepIcon = selectedStep.icon;

  return (
    <div>
      <section className="relative isolate overflow-hidden bg-slate-50 px-5 py-20 md:px-8 md:py-24 lg:px-12 lg:py-28">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_8%_14%,rgba(100,60,144,0.12),transparent_28%),radial-gradient(circle_at_92%_82%,rgba(255,219,22,0.16),transparent_26%)]" />
        <div className="absolute inset-0 -z-10 opacity-[0.045] [background-image:linear-gradient(rgba(15,23,42,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.5)_1px,transparent_1px)] [background-size:64px_64px]" />

        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
              <span className="h-2.5 w-2.5 rounded-full bg-[#643c90]" />
              <span className="text-sm font-bold tracking-wide text-slate-700">
                How we work together
              </span>
            </div>
            <h2 className="mt-6 font-heading text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-slate-950 md:text-5xl lg:text-[3.6rem]">
              Collaboration, from
              <span className="block text-[#643c90]">concept to code.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-600">
              A transparent, collaborative process keeps you involved at every
              stage and gives every decision a clear purpose.
            </p>
          </div>

          <div
            role="tablist"
            aria-label="Website development process"
            className="mt-12 flex gap-3 overflow-x-auto pb-3 sm:grid sm:grid-cols-5 sm:overflow-visible sm:pb-0"
          >
            {processSteps.map(({ name, icon: Icon }, index) => {
              const isSelected = selectedImage === index;

              return (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  aria-controls="website-process-detail"
                  onClick={() => setSelectedImage(index)}
                  className={`group min-w-[145px] rounded-2xl border p-4 text-left transition duration-300 sm:min-w-0 sm:text-center ${
                    isSelected
                      ? "border-[#643c90] bg-[#643c90] text-white shadow-[0_16px_40px_rgba(100,60,144,0.22)]"
                      : "border-slate-200 bg-white text-slate-700 hover:-translate-y-1 hover:border-[#643c90]/35 hover:shadow-lg"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-xl sm:mx-auto ${
                      isSelected
                        ? "bg-[#FFDB16] text-slate-950"
                        : "bg-slate-100 text-[#643c90] group-hover:bg-[#643c90]/10"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="mt-3 block text-[10px] font-extrabold uppercase tracking-[0.15em] opacity-60">
                    Step {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="mt-1 block font-heading text-sm font-extrabold sm:text-base">
                    {name}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            id="website-process-detail"
            role="tabpanel"
            className="mt-5 grid overflow-hidden rounded-[28px] bg-[#080b12] text-white shadow-2xl shadow-slate-300/50 lg:grid-cols-[1.05fr_0.95fr]"
          >
            <div className="relative overflow-hidden p-7 md:p-10 lg:p-12">
              <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-[#643c90]/30 blur-3xl" />
              <div className="relative">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFDB16] text-slate-950 shadow-[0_14px_35px_rgba(255,219,22,0.2)]">
                  <SelectedStepIcon className="h-6 w-6" />
                </div>
                <p className="mt-7 text-xs font-extrabold uppercase tracking-[0.16em] text-[#FFDB16]">
                  {selectedStep.label}
                </p>
                <h3 className="mt-3 font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
                  {selectedStep.name}
                </h3>
                <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-300">
                  {selectedStep.description}
                </p>
              </div>
            </div>

            <div className="border-t border-white/10 bg-white/[0.055] p-7 md:p-10 lg:border-l lg:border-t-0 lg:p-12">
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500">
                What you can expect
              </p>
              <div className="mt-6 space-y-4">
                {selectedStep.outcomes.map((outcome) => (
                  <div
                    key={outcome}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.055] p-4"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FFDB16]/15 text-[#FFDB16]">
                      <FiCheck className="h-4 w-4" />
                    </span>
                    <span className="font-semibold text-slate-200">{outcome}</span>
                  </div>
                ))}
              </div>
              <p className="mt-7 border-l-2 border-[#FFDB16] pl-4 text-sm leading-6 text-slate-400">
                You always know what is happening, why it matters, and what comes next.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="px-[5%] py-[5%] bg-[#13152c]">
        <h2 className="type-section-title text-center text-white mb-[20px]">
          Our Happy Clients
        </h2>

        <div className="mt-[5%]">
          <SwiperSlide>
            <div
              style={{
                marginTop: "120px",
                color: "#fbcc0d",
                marginLeft: "-30px",
                zIndex: "1",
              }}
              className="swiper-button-prev "
            ></div>
          </SwiperSlide>
          <Swiper
            cssMode={true}
            // navigation={true}
            navigation={{
              prevEl: ".swiper-button-prev",
              nextEl: ".swiper-button-next",
            }}
            //   pagination={true}
            mousewheel={true}
            keyboard={true}
            breakpoints={{
              640: { slidesPerView: 1 },
              778: { slidesPerView: 2 },
              1024: { slidesPerView: 2 },
              1224: { slidesPerView: 3 },
              1324: { slidesPerView: 3 },

              1524: { slidesPerView: 3 },
            }}
            modules={[Navigation, Mousewheel, Keyboard]}
            className="mySwiper"
          >
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Harshil Mathur"
                companyName="IIFEM"
                feedback="I recently hired Abscod Infomatics to develop a website for my business, and I was thoroughly impressed with the level of professionalism and expertise displayed throughout the entire process."
              />
            </SwiperSlide>
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Gaurav Munjal"
                companyName="Cevious Technologies"
                feedback="I had been struggling to find a reliable and trustworthy website development service until I came across Abscod Infomatics. Their team was extremely helpful and communicative, guiding me through each step of the development process and ensuring."
              />
            </SwiperSlide>
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Zender Lama"
                companyName="Kulmaya"
                feedback="Working with Abscod Infomatics was a seamless experience from start to finish. Their team was incredibly responsive and attentive to my needs, and they delivered a website that not only looked great but also functioned flawlessly."
              />
            </SwiperSlide>
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Amit Chauhan"
                companyName="DG Collection"
                feedback="Abscod Infomatics exceeded my expectations in every way possible. Their team was incredibly knowledgeable and skilled, and they were able to develop a website that perfectly aligned with my vision for my business. "
              />
            </SwiperSlide>
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Saurabh Tanwani"
                companyName="Crabgrass"
                feedback="Abscod Infomatics provided exceptional website development services for my business. Their attention to detail and dedication to ensuring that every aspect of the website was functioning properly was impressive. I would definitely recommend them ."
              />
            </SwiperSlide>
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Saurabh Trivedi"
                companyName="24 India Times"
                feedback="I am extremely happy with the website that Abscod Infomatics developed for my business. Not only does it look great, but it also functions perfectly and has helped me to increase my online visibility and drive more traffic to my site."
              />
            </SwiperSlide>
            <SwiperSlide>
              <WebsiteClientFeedbackCart
                clientName="Amit Aggarwal"
                companyName="Effectual Services"
                feedback="Abscod Infomatics was a pleasure to work with. They were always available to answer my questions and address any concerns that I had, and they delivered the website within the agreed-upon timeframe. I would definitely work with them again in the future."
              />
            </SwiperSlide>
          </Swiper>
          <SwiperSlide>
            <div
              style={{
                marginTop: "-189px",
                color: "#fbcc0d",
                marginRight: "-30px",
                zIndex: "1",
              }}
              className="swiper-button-next "
            ></div>
          </SwiperSlide>
        </div>
      </div>
    </div>
  );
};

export default WebsiteClientFeedback;
